import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Check, Lock, X } from "lucide-react";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { BrandButton } from "@/components/brand/brand-button";
import { BrandInput } from "@/components/brand/brand-input";
import { Logo } from "@/components/brand/logo";
import { PublicFooter } from "@/components/public-layout";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { buildLocalizedSeoLinks, getLocalizedSeoUrls } from "@/lib/seo";
import { passwordSchema } from "@/lib/validation";
import { authClient } from "../lib/auth-client";

export const Route = createFileRoute("/reset-password")({
	validateSearch: (search: Record<string, unknown>) => ({
		token:
			typeof search.token === "string" && search.token
				? search.token
				: undefined,
		error:
			typeof search.error === "string" && search.error
				? search.error
				: undefined,
	}),
	component: ResetPasswordPage,
	head: ({ matches }) => {
		const seoUrls = getLocalizedSeoUrls({
			matches,
			internalPath: "/reset-password",
		});
		const title = "Set a new password - AgentMRR";
		const description = "Choose a new password for your AgentMRR account.";
		return {
			meta: [
				{ title },
				{ name: "description", content: description },
				{ name: "robots", content: "noindex, nofollow" },
				{ property: "og:title", content: title },
				{ property: "og:description", content: description },
				{ property: "og:url", content: seoUrls.canonicalUrl },
				{ property: "og:type", content: "website" },
			],
			links: buildLocalizedSeoLinks({
				matches,
				internalPath: "/reset-password",
			}),
		};
	},
});

const resetPasswordSchema = z
	.object({
		password: passwordSchema,
		confirmPassword: z.string().min(1, "Confirm your password"),
	})
	.refine((data) => data.password === data.confirmPassword, {
		message: "Passwords do not match",
		path: ["confirmPassword"],
	});

type ResetPasswordForm = z.infer<typeof resetPasswordSchema>;

function getPasswordStrength(password: string): {
	score: number;
	label: string;
	color: string;
} {
	let score = 0;
	if (password.length >= 8) score++;
	if (password.length >= 12) score++;
	if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
	if (/\d/.test(password)) score++;
	if (/[^a-zA-Z0-9]/.test(password)) score++;

	if (score <= 1)
		return { score: 1, label: "Weak", color: "bg-[color:var(--ticker-down)]" };
	if (score <= 2) return { score: 2, label: "Fair", color: "bg-amber-500" };
	if (score <= 3) return { score: 3, label: "Medium", color: "bg-amber-400" };
	if (score <= 4) return { score: 4, label: "Strong", color: "bg-brand" };
	return { score: 5, label: "Very strong", color: "bg-brand" };
}

function ResetPasswordPage() {
	const { token, error: tokenError } = Route.useSearch();
	const [success, setSuccess] = useState(false);
	const [invalidToken, setInvalidToken] = useState(!token || !!tokenError);

	const form = useForm<ResetPasswordForm>({
		resolver: zodResolver(resetPasswordSchema),
		mode: "onSubmit",
		reValidateMode: "onChange",
		defaultValues: { password: "", confirmPassword: "" },
	});

	const password = form.watch("password");
	const passwordStrength = useMemo(
		() => getPasswordStrength(password ?? ""),
		[password],
	);

	const onSubmit = async (values: ResetPasswordForm) => {
		try {
			const { error: apiError } = await authClient.resetPassword({
				newPassword: values.password,
				token,
			});
			if (apiError) {
				const message = apiError.message || "Failed to reset password";
				if (
					message.toLowerCase().includes("token") ||
					message.toLowerCase().includes("expired")
				) {
					setInvalidToken(true);
				} else {
					form.setError("root.serverError", { message });
				}
				return;
			}
			setSuccess(true);
		} catch (err) {
			const message =
				err instanceof Error ? err.message : "Failed to reset password";
			if (
				message.toLowerCase().includes("token") ||
				message.toLowerCase().includes("expired")
			) {
				setInvalidToken(true);
			} else {
				form.setError("root.serverError", { message });
			}
		}
	};

	const loading = form.formState.isSubmitting;
	const serverError = form.formState.errors.root?.serverError?.message;

	return (
		<div className="flex min-h-svh flex-col bg-background text-foreground">
			<main className="flex flex-1 items-center justify-center px-4 py-12">
				<div className="w-full max-w-[420px]">
					<div className="mb-8 flex items-center justify-between">
						<Logo size="sm" disableLink />
						<span className="font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground">
							&gt; Auth / reset
						</span>
					</div>

					<div className="border border-border bg-card p-6 sm:p-8">
						{success ? (
							<div className="space-y-5 text-center">
								<div className="mx-auto inline-flex size-10 items-center justify-center border border-brand/40 bg-brand-soft">
									<Check className="size-4 text-brand" />
								</div>
								<div className="space-y-1.5">
									<h2
										className="font-mono text-xl leading-tight tracking-[-0.02em]"
										style={{ fontWeight: 400 }}
									>
										<span className="text-brand">/</span>password-updated
									</h2>
									<p className="text-base text-muted-foreground">
										Password updated. Sign in to continue.
									</p>
								</div>
								<BrandButton asChild size="lg" className="w-full">
									<Link to="/login">Sign in</Link>
								</BrandButton>
							</div>
						) : invalidToken ? (
							<div className="space-y-5 text-center">
								<div className="mx-auto inline-flex size-10 items-center justify-center border border-[color:var(--ticker-down)]/40 bg-destructive/10">
									<X className="size-4 text-[color:var(--ticker-down)]" />
								</div>
								<div className="space-y-1.5">
									<h2
										className="font-mono text-xl leading-tight tracking-[-0.02em]"
										style={{ fontWeight: 400 }}
									>
										<span className="text-brand">/</span>link-expired
									</h2>
									<p className="text-base text-muted-foreground">
										This password reset link is invalid or has expired.
									</p>
								</div>
								<BrandButton asChild size="lg" className="w-full">
									<Link to="/forgot-password">Request a new link</Link>
								</BrandButton>
							</div>
						) : (
							<>
								<div className="space-y-2">
									<h2
										className="font-mono text-2xl leading-tight tracking-[-0.02em]"
										style={{ fontWeight: 400 }}
									>
										<span className="text-brand">/</span>new-password
									</h2>
									<p className="text-base text-muted-foreground">
										Choose a strong password for your account.
									</p>
								</div>

								{serverError && (
									<div className="mt-5 border border-destructive/30 bg-destructive/10 px-3 py-2 font-mono text-sm uppercase tracking-[0.12em] text-destructive">
										{serverError}
									</div>
								)}

								<Form {...form}>
									<form
										onSubmit={form.handleSubmit(onSubmit)}
										className="mt-6 space-y-5"
										noValidate
									>
										<FormField
											control={form.control}
											name="password"
											render={({ field }) => (
												<FormItem>
													<FormLabel>New password</FormLabel>
													<div className="relative">
														<Lock className="absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
														<FormControl>
															<BrandInput
																autoFocus
																type="password"
																autoComplete="new-password"
																placeholder="min 8 chars"
																className="pl-9"
																{...field}
															/>
														</FormControl>
													</div>
													{field.value && (
														<div className="mt-2 flex items-center gap-3">
															<div className="flex flex-1 gap-1">
																{[1, 2, 3, 4, 5].map((level) => (
																	<div
																		key={level}
																		className={`h-0.5 flex-1 transition-colors ${
																			level <= passwordStrength.score
																				? passwordStrength.color
																				: "bg-muted"
																		}`}
																	/>
																))}
															</div>
															<span className="shrink-0 font-mono text-sm uppercase tracking-[0.12em] text-muted-foreground">
																{passwordStrength.label}
															</span>
														</div>
													)}
													<FormMessage />
												</FormItem>
											)}
										/>

										<FormField
											control={form.control}
											name="confirmPassword"
											render={({ field }) => (
												<FormItem>
													<FormLabel>Confirm password</FormLabel>
													<div className="relative">
														<Lock className="absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
														<FormControl>
															<BrandInput
																type="password"
																autoComplete="new-password"
																placeholder="••••••••"
																className="pl-9"
																{...field}
															/>
														</FormControl>
													</div>
													<FormMessage />
												</FormItem>
											)}
										/>

										<BrandButton
											type="submit"
											size="lg"
											disabled={loading}
											className="w-full"
										>
											{loading ? "Resetting" : "Reset password"}
										</BrandButton>
									</form>
								</Form>
							</>
						)}
					</div>
				</div>
			</main>
			<PublicFooter />
		</div>
	);
}
